class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();

    /*
     * Auto-réparation : si le tiroir est rendu visible (par le thème, une application ou le clic sur l'icône)
     * alors que son contenu ne correspond pas à l'état du panier (ancien écran « panier vide »),
     * on recharge le contenu depuis Shopify au lieu d'afficher une page blanche.
     */
    this.classObserver = new MutationObserver(() => {
      if (this.classList.contains('active') && this.isStale()) this.refresh();
    });
    this.classObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.dnBound) {
      overlay.addEventListener('click', this.close.bind(this));
      overlay.dataset.dnBound = 'true';
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* Le contenu affiché est « périmé » quand l'écran « panier vide » est présent alors que le tiroir n'est plus marqué vide, ou l'inverse */
  isStale() {
    const hasEmptyScreen = !!this.querySelector('.drawer__inner-empty');
    const hasItems = !!this.querySelector('#CartDrawer-CartItems .cart-item');
    const markedEmpty = this.classList.contains('is-empty');
    if (hasEmptyScreen && !markedEmpty) return true;
    if (!hasEmptyScreen && !hasItems && !markedEmpty) return true;
    if (hasItems && markedEmpty) return true;
    return false;
  }

  /* Aligne la classe is-empty sur le contenu réellement affiché */
  syncEmptyState() {
    const isEmpty = !!this.querySelector('.drawer__inner-empty') && !this.querySelector('#CartDrawer-CartItems .cart-item');
    this.classList.toggle('is-empty', isEmpty);
    const items = this.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', isEmpty);
  }

  /* Recharge le tiroir et la pastille du panier depuis Shopify (Section Rendering API) */
  refresh() {
    if (this.refreshing) return this.refreshing;
    const url = `${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`;
    this.refreshing = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((sections) => {
        this.applySections(sections);
      })
      .catch((e) => {
        console.error('[cart-drawer] rechargement impossible, rechargement de la page', e);
        window.location.reload();
      })
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  /* Remplace le contenu à partir d'un objet { 'cart-drawer': html, 'cart-icon-bubble': html } ; renvoie false si incomplet */
  applySections(sections) {
    if (!sections || !sections['cart-drawer']) return false;
    let ok = true;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !sections[section.id]) return;
      const html = this.getSectionInnerHTML(sections[section.id], section.selector);
      if (html === null) {
        ok = false;
        return;
      }
      sectionElement.innerHTML = html;
    });
    this.bindOverlay();
    this.syncEmptyState();
    return ok;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.isStale()) this.refresh();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /*
   * Appelé après un ajout au panier. Si la réponse d'ajout ne contient pas le rendu du tiroir,
   * ou si le remplacement échoue, on recharge depuis Shopify avant d'ouvrir : plus d'écran blanc.
   */
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;

    let rendered = false;
    try {
      rendered = this.applySections(parsedState && parsedState.sections);
    } catch (e) {
      console.error('[cart-drawer] rendu après ajout impossible', e);
      rendered = false;
    }

    const openDrawer = () => setTimeout(() => this.open());

    if (rendered && !this.isStale()) {
      openDrawer();
    } else {
      this.refresh().then(openDrawer);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);